// Transactions.js
import React, { useContext, useEffect, useState, useMemo, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import TransactionsBox from "./TransactionsBox";
import ButtonBar from "../../Elements/ButtonBar";
import { VARIANTES_BOTON } from "../../Elements/Button";
import ConfirmDialog from "../../Elements/ConfirmDialog";
import { UserContext } from "../../../context/UserContext";
import { formatApiDateTime, parseApiDateTime } from "../../../lib/helpers";
import { cantidadDespachada, notaPedidoMerma, esPeso } from '../../../lib/helpers/products';
import { PERIODO_INICIAL, cotaDeFecha, horaDeCorte, queryDeTransacciones, ventanaDeCarga, ventanaDeJornada } from '../../../lib/ventanaTransacciones';

// Cuánto se espera la hora de corte del club antes de abrir con el día natural: la pantalla no
// puede quedarse en «Cargando…» por una lectura de configuración colgada (review 09-10-2026).
const ESPERA_CORTE_MS = 4000;

export default function Transactions() {
  const [transactions, setTransactions] = useState([]);
  // La ventana con la que se PIDIERON las filas cargadas. El recorte del navegador filtra con
  // ella y no con «la jornada de ahora»: con la pantalla abierta al cruzar el corte, recalcularla
  // vaciaba la tabla y ponía los chips a cero (review 09-10-2026).
  const [ventanaCargada, setVentanaCargada] = useState({});
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  // El movimiento pendiente de confirmar, y cuál se está enviando ya.
  const [porAnular, setPorAnular] = useState(null);
  const [anulando, setAnulando] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [token, , , venueID] = useContext(UserContext);
  
  // Filtros
  const [searchInput, setSearchInput] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, sale, credit, refund
  // Método de pago (petición Kong, 09-08): para cuadrar caja hace falta aislar el
  // efectivo — solo ese entra en la caja. El dato viaja en cada transacción.
  const [filterPayment, setFilterPayment] = useState("all"); // all, cash, card, credit
  // Abre en la JORNADA de hoy del club (09-10-2026; antes 30 días, 04-10-2026; antes «todo»):
  // pedir el historial entero en cada apertura atascaba la API por la noche, y el mes de Kong
  // Club eran ~6,6 MB y 3-4 s en la tablet para mirar el turno. «Todas las fechas» sigue ahí,
  // pero es una elección explícita.
  const [filterDate, setFilterDate] = useState(PERIODO_INICIAL); // all, jornada, week, month
  const [draftFromDate, setDraftFromDate] = useState("");
  const [draftToDate, setDraftToDate] = useState("");
  const [customFromDate, setCustomFromDate] = useState("");
  const [customToDate, setCustomToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const getTransactionDate = useCallback((tx) => {
    const rawDate = tx.timestamp || tx.created_at;
    const parsedDate = rawDate ? parseApiDateTime(rawDate) : null;
    return parsedDate && !Number.isNaN(parsedDate.getTime()) ? parsedDate : null;
  }, []);

  // Un dato, un render: método de pago legible para la tabla Y para el Excel.
  const getPaymentLabel = useCallback((method) => {
    const key = String(method || "").toLowerCase();
    return { cash: "Efectivo", card: "Tarjeta", transfer: "Transferencia", credit: "Saldo del socio" }[key] || "—";
  }, []);

  const getTransactionTypeLabel = useCallback((type, concept) => {
    // La cuota de inscripción viaja como REFUND (resta saldo) pero NO es dinero
    // devuelto: se etiqueta y se cuenta aparte para no ensuciar la contabilidad.
    if (concept === 'membership_fee') return "Cuota de inscripción";
    const normalizedType = type?.toLowerCase();
    if (normalizedType === "purchase" || normalizedType === "sale" || normalizedType === "venta") {
      return "Venta";
    }
    if (
      normalizedType === "credit" ||
      normalizedType === "credito" ||
      normalizedType === "crédito" ||
      normalizedType === "add_cash"
    ) {
      return "Aportación";
    }
    if (normalizedType === "refund" || normalizedType === "reembolso") {
      return "Reembolso";
    }
    return type || "-";
  }, []);

  const formatDateForExport = useCallback(
    (value) => formatApiDateTime(value, { fallback: "-" }),
    []
  );

  // Corte de jornada del club (0-23) para los presets "Jornada de hoy/ayer".
  // null = aún sin respuesta del backend: los presets se deshabilitan para no
  // calcular una jornada con corte 00:00 que no es el del club.
  // El corte va PEGADO al club al que pertenece y se deriva en el render: al cambiar de club es
  // null en ese mismo render, así que la jornada no se pide con el corte del club anterior (un
  // reset en un efecto llegaba tarde: el efecto que pide ya había salido en el mismo commit).
  // Sin club (superadmin sin elegir, cuenta sin tienda): día natural, sin esperar a nada.
  const [corteDelClub, setCorteDelClub] = useState({ venueID: null, hour: null });
  const dayCutoffHour = !venueID ? 0 : (corteDelClub.venueID === venueID ? corteDelClub.hour : null);
  useEffect(() => {
    if (!token || !venueID) return;
    let cancelled = false;
    const abortador = new AbortController();
    const limite = setTimeout(() => abortador.abort(), ESPERA_CORTE_MS);
    (async () => {
      let hour = 0; // fallo, lentitud o corte sin configurar: día natural, pero explícito
      try {
        const res = await fetch(`/v1/venues/${venueID}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortador.signal,
        });
        if (res.ok) {
          const data = await res.json();
          hour = Number(data.day_cutoff_hour ?? 0);
        }
      } catch { /* sin red o demasiado lento: día natural */ }
      clearTimeout(limite);
      if (!cancelled) setCorteDelClub({ venueID, hour });
    })();
    return () => {
      cancelled = true;
      clearTimeout(limite);
      abortador.abort();
    };
  }, [token, venueID]);

  // Rellena la horquilla con una jornada completa: [corte, corte+24h).
  const toLocalInput = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const applyJornada = useCallback((offsetDays) => {
    if (dayCutoffHour == null) return; // corte aún sin cargar (los botones están disabled)
    // La MISMA jornada que el periodo inicial y el recorte del navegador (lib/ventanaTransacciones).
    // `hasta` es el último ms antes del corte; el campo, al minuto, queda en HH:59.
    const { desde, hasta } = ventanaDeJornada(dayCutoffHour, offsetDays);
    const from = toLocalInput(desde);
    const to = toLocalInput(hasta);
    setDraftFromDate(from);
    setDraftToDate(to);
    setCustomFromDate(from);
    setCustomToDate(to);
    setFilterDate("all");
    setErrorMessage("");
  }, [dayCutoffHour]);

  const applyCustomDateRange = useCallback(() => {
    const fromBoundary = cotaDeFecha(draftFromDate, "start");
    const toBoundary = cotaDeFecha(draftToDate, "end");

    if (fromBoundary && toBoundary && fromBoundary > toBoundary) {
      setErrorMessage("La fecha 'Desde' no puede ser mayor que 'Hasta'.");
      return;
    }

    setErrorMessage("");
    setCustomFromDate(draftFromDate);
    setCustomToDate(draftToDate);
    setFilterDate("all");
  }, [draftFromDate, draftToDate]);

  const clearCustomDateRange = useCallback(() => {
    setDraftFromDate("");
    setDraftToDate("");
    setCustomFromDate("");
    setCustomToDate("");
    // Tras una jornada o un rango el periodo queda en «Todas las fechas»: limpiar y quedarse
    // ahí pediría el historial entero sin que nadie lo haya decidido. Pero quien ELIGIÓ
    // «Todas las fechas» y no aplicó ningún rango no ha pedido que se le estreche (review).
    if (customFromDate || customToDate) setFilterDate(PERIODO_INICIAL);
    setErrorMessage("");
  }, [customFromDate, customToDate]);

  // «Ver todas las fechas» desde el aviso del periodo: elección explícita del historial completo.
  const verTodasLasFechas = useCallback(() => {
    setDraftFromDate("");
    setDraftToDate("");
    setCustomFromDate("");
    setCustomToDate("");
    setFilterDate("all");
    setErrorMessage("");
  }, []);

  // Número de la última petición lanzada. Cambiar de periodo vuelve a pedir, y la respuesta de
  // «Todas las fechas» (la lenta) puede llegar DESPUÉS de la de «Última semana»: sin esta guarda pisaba
  // lo bueno con datos de otra ventana, o su error vaciaba la lista (review). Antes la pantalla
  // pedía una sola vez y esta carrera no podía darse.
  const peticionRef = useRef(0);

  // Solo la jornada depende del corte: los demás periodos no deben volver a pedir cuando llega.
  const corteDeLaVentana = filterDate === "jornada" ? dayCutoffHour : 0;

  const fetchTransactions = useCallback(async () => {
    if (!token) {
      setErrorMessage('No hay token de autenticación');
      setLoaded(true);
      return;
    }

    // La jornada necesita la hora de corte del club. Hasta que llega (una petición pequeña a
    // /v1/venues/{id}) no se pide nada: con un corte provisional se pediría una ventana y luego
    // la buena, y la pantalla cargaría dos veces. Si esa lectura falla, el corte es 0 (día natural).
    if (corteDeLaVentana == null) {
      setLoaded(false);
      return;
    }

    const idPeticion = ++peticionRef.current;
    const esLaVigente = () => idPeticion === peticionRef.current;
    // La ventana se calcula AQUÍ, con la hora de ahora: «Recargar» tras horas con la pantalla
    // abierta (o pasado el corte) pide la jornada de ahora, no la de cuando se eligió el filtro.
    const ventana = ventanaDeCarga({
      filterDate, customFrom: customFromDate, customTo: customToDate, corte: corteDeLaVentana,
    });

    setLoaded(false);
    setErrorMessage("");
    
    try {
      const res = await fetch(`/v1/get-all-transactions${queryDeTransacciones(venueID, ventana)}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ detail: 'Error al obtener transacciones' }));
        throw new Error(errorData.detail || "No tienes privilegios para ver esto");
      }
      
      const data = await res.json();
      if (!esLaVigente()) return;
      let txList = Array.isArray(data.transactions) ? data.transactions : (Array.isArray(data) ? data : []);
      // No incluir operaciones anuladas (reversed) en la lista de Transacciones
      txList = txList.filter(tx => (tx.status || '').toLowerCase() !== 'reversed');
      setVentanaCargada(ventana);
      setTransactions(txList);
      setErrorMessage("");
    } catch (err) {
      if (!esLaVigente()) return;
      console.error("Failed to fetch transactions:", err);
      setErrorMessage(err.message || "Error al cargar transacciones");
      setTransactions([]);
    } finally {
      if (esLaVigente()) setLoaded(true);
    }
  }, [token, venueID, filterDate, customFromDate, customToDate, corteDeLaVentana]); // Recarga al cambiar tienda, ventana o (solo en jornada) corte

  // Anular una venta: devuelve stock + saldo y la oculta de la lista (status reversed).
  // Anular cualquier movimiento reversible: venta, aportación o cuota de
  // inscripción. Antes solo se podían anular ventas y las aportaciones mal
  // metidas había que corregirlas a mano (petición Kong, 11-08).
  // Qué se anula, a dónde va y qué se le cuenta al staff ANTES de tocar dinero.
  // Aparte del envío, para poder pintarlo en el diálogo.
  const planDeAnulacion = useCallback((transaction) => {
    const type = String(transaction.type || "").toLowerCase();
    const isDeposit = ["credit", "credito", "crédito", "add_cash"].includes(type);
    const isFee = transaction.concept === "membership_fee";

    if (isFee) {
      return { url: `/v1/reverse-inscription-fee/${transaction.id}`,
        titulo: "Anular la cuota de inscripción",
        descripcion: "Se le devolverá el importe al saldo del socio.",
        boton: "Anular la cuota",
        err: "No se pudo anular la cuota" };
    }
    if (isDeposit) {
      return { url: `/v1/reverse-add-cash/${transaction.id}`,
        titulo: "Anular la aportación",
        descripcion: "Se restará del saldo del socio y, si fue en efectivo, también de la caja del turno.",
        // Desde 07-09-2026 se puede anular aunque el socio se haya gastado ya ese
        // dinero: el saldo queda en negativo, que es deuda (BE, petición de Kong).
        // Va como aviso aparte para que nadie lo lea como un error de la app.
        nota: "Si el socio ya se gastó ese dinero, su saldo quedará en negativo (deuda).",
        boton: "Anular la aportación",
        err: "No se pudo anular la aportación" };
    }
    return { url: `/v1/reverse-purchase/${transaction.id}`,
      titulo: "Anular la venta",
      descripcion: "Se devolverá el stock y, si fue a crédito, el saldo al socio.",
      boton: "Anular la venta",
      err: "No se pudo anular la venta" };
  }, []);

  // ConfirmDialog y no `window.confirm`: el nativo se traga en las tablets del club,
  // y este botón MUEVE DINERO — allí estaba muerto y sin decir nada (08-09-2026).
  const handleReverse = useCallback(async (transaction) => {
    if (!transaction?.id) return;
    const plan = planDeAnulacion(transaction);
    setAnulando(transaction.id);
    try {
      const res = await fetch(plan.url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...(venueID && { "x-venue-id": venueID }),
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || plan.err);
      await fetchTransactions();
    } catch (err) {
      setErrorMessage(err.message || "No se pudo anular el movimiento");
    } finally {
      setAnulando(null);
      setPorAnular(null);
    }
  }, [token, venueID, fetchTransactions, planDeAnulacion]);

  const exportFilteredTransactions = useCallback(() => {
    if (filteredTransactions.length === 0) {
      setErrorMessage("No hay transacciones filtradas para exportar.");
      return;
    }

    setErrorMessage("");

    const rows = filteredTransactions.map((tx) => {
      const items = Array.isArray(tx.items) ? tx.items : [];
      const productsSummary = items.length > 0
        ? items
            .map((item) => {
              const productName = item.product_name || `Producto #${item.product_id || "-"}`;
              const quantity = Number(item.quantity || 0);
              const unitPrice = Number(item.unit_price || 0);
              const lineTotal = quantity * unitPrice;   // el € va por lo PEDIDO
              const despachado = cantidadDespachada(item);
              const nota = notaPedidoMerma(item);
              return `${productName} x${despachado}${esPeso(item) ? 'g' : ''}${nota ? ` [${nota}]` : ''} (${unitPrice.toFixed(2)} EUR) = ${lineTotal.toFixed(2)} EUR`;
            })
            .join(" | ")
        : "Sin items";

      return {
        ID: tx.id || "-",
        Usuario: tx.user_name || "-",
        Email: tx.user_email || "-",
        "Atendido por": tx.processed_by_name || "-",
        "ID usuario": tx.user_id || "-",
        Tienda: tx.shop_name || "-",
        "ID tienda": tx.shop_id || "-",
        Tipo: getTransactionTypeLabel(tx.type, tx.concept),
        Pago: getPaymentLabel(tx.payment_method),
        Estado: tx.status || "-",
        Monto: Number(tx.amount || 0),
        Fecha: formatDateForExport(tx.timestamp || tx.created_at),
        Productos: productsSummary,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transacciones");

    const exportDate = new Date().toISOString().slice(0, 10);
    const filename = `transacciones-filtradas-${exportDate}.xlsx`;
    XLSX.writeFile(workbook, filename);
  }, [filteredTransactions, formatDateForExport, getTransactionTypeLabel, getPaymentLabel]);

  useEffect(() => {
    if (token) fetchTransactions();
  }, [token, venueID, fetchTransactions]); // Recargar cuando cambie el token o venueID

  // Filtrado en tiempo real
  useEffect(() => {
    let filtered = [...transactions];

    // No mostrar operaciones anuladas (ej. add_cash deshecho)
    filtered = filtered.filter(tx => (tx.status || '').toLowerCase() !== 'reversed');

    // Filtrar por búsqueda
    if (searchInput && searchInput.trim() !== "") {
      const searchTerm = searchInput.toLowerCase();
      filtered = filtered.filter(tx => {
        // Solo campos con sentido para el staff. NO se busca contra UUIDs
        // (user_id/shop_id): al ser hex, un término numérico como "14" casaba con
        // UUIDs al azar y salían transacciones que no tocaban (bug del filtro).
        const txId = (tx.id?.toString() || '').slice(0, 8).toLowerCase(); // ID corto mostrado (#xxxxxxxx)
        const userName = (tx.user_name || '').toLowerCase();
        const userEmail = (tx.user_email || '').toLowerCase();
        const shopName = (tx.shop_name || '').toLowerCase();
        const amount = tx.amount?.toString() || '';
        // Nº socio (visible = legacy || interno) para poder buscar por número, p.ej. "14"
        const membershipId = tx.membership_id != null ? String(tx.membership_id) : '';
        const legacyMemberId = (tx.legacy_member_id ? String(tx.legacy_member_id) : '').toLowerCase();

        return (
          txId.includes(searchTerm) ||
          userName.includes(searchTerm) ||
          userEmail.includes(searchTerm) ||
          shopName.includes(searchTerm) ||
          amount.includes(searchTerm) ||
          membershipId.includes(searchTerm) ||
          legacyMemberId.includes(searchTerm)
        );
      });
    }

    // Filtrar por tipo
    if (filterType !== "all") {
      filtered = filtered.filter(tx => {
        const txType = tx.type?.toLowerCase();
        // Mapear términos en español e inglés, incluyendo add_cash y purchase
        if (filterType === "venta" && (txType === "purchase" || txType === "sale" || txType === "venta")) return true;
        if (filterType === "credito" && (txType === "credit" || txType === "credito" || txType === "crédito" || txType === "add_cash")) return true;
        if (filterType === "cuota") return tx.concept === 'membership_fee';
        if (filterType === "reembolso") {
          return (txType === "refund" || txType === "reembolso") && tx.concept !== 'membership_fee';
        }
        return txType === filterType;
      });
    }

    // Filtrar por método de pago (efectivo / tarjeta / saldo del socio)
    if (filterPayment !== "all") {
      filtered = filtered.filter(
        (tx) => String(tx.payment_method || "").toLowerCase() === filterPayment
      );
    }

    // Filtrar por periodo predefinido con la MISMA ventana que se pidió al servidor (guardada al
    // cargar), con sus dos cotas. No se recalcula con la hora de ahora: al cruzar el corte con la
    // pantalla abierta dejaría fuera todo lo cargado.
    if (filterDate !== "all") {
      const desde = ventanaCargada.date_from ? new Date(ventanaCargada.date_from) : null;
      const hasta = ventanaCargada.date_to ? new Date(ventanaCargada.date_to) : null;
      filtered = filtered.filter(tx => {
        const txDate = getTransactionDate(tx);
        if (!txDate) return false;
        if (desde && txDate < desde) return false;
        if (hasta && txDate > hasta) return false;
        return true;
      });
    }

    if (customFromDate || customToDate) {
      const fromBoundary = cotaDeFecha(customFromDate, "start");
      const toBoundary = cotaDeFecha(customToDate, "end");

      filtered = filtered.filter((tx) => {
        const txDate = getTransactionDate(tx);
        if (!txDate) return false;
        if (fromBoundary && txDate < fromBoundary) return false;
        if (toBoundary && txDate > toBoundary) return false;
        return true;
      });
    }

    // ✅ ORDENAR por fecha descendente (más recientes primero)
    filtered.sort((a, b) => {
      const dateA = getTransactionDate(a) || new Date(0);
      const dateB = getTransactionDate(b) || new Date(0);
      return dateB - dateA; // Orden descendente
    });
    setFilteredTransactions(filtered);
    setCurrentPage(1); // Reset a la primera página al filtrar
  }, [searchInput, filterType, filterPayment, filterDate, ventanaCargada, transactions, customFromDate, customToDate, getTransactionDate]);

  // Estadísticas. IMPORTANTE (contabilidad): ventas y aportaciones NO se suman —
  // el socio aporta 20 € y luego consume 20 €: es el MISMO dinero, y sumarlo
  // duplicaba la facturación del día. Por eso cada tipo lleva su propio importe y
  // el total mezclado solo se muestra cuando el filtro deja un único tipo.
  const stats = useMemo(() => {
    const isSale = (tx) => ['purchase', 'sale', 'venta'].includes(tx.type?.toLowerCase());
    const isDeposit = (tx) => ['credit', 'credito', 'crédito', 'add_cash'].includes(tx.type?.toLowerCase());
    const isFee = (tx) => tx.concept === 'membership_fee';
    const isRefund = (tx) => !isFee(tx) && ['refund', 'reembolso'].includes(tx.type?.toLowerCase());
    const sum = (rows) => rows.reduce((acc, tx) => acc + (parseFloat(tx.amount) || 0), 0);

    const salesRows = filteredTransactions.filter(isSale);
    const depositRows = filteredTransactions.filter(isDeposit);
    const refundRows = filteredTransactions.filter(isRefund);
    const feeRows = filteredTransactions.filter(isFee);

    return {
      total: sum(filteredTransactions).toFixed(2),
      count: filteredTransactions.length,
      sales: salesRows.length,
      credits: depositRows.length,
      refunds: refundRows.length,
      salesAmount: sum(salesRows).toFixed(2),
      depositsAmount: sum(depositRows).toFixed(2),
      refundsAmount: sum(refundRows).toFixed(2),
      fees: feeRows.length,
      feesAmount: sum(feeRows).toFixed(2),
    };
  }, [filteredTransactions]);

  const etiquetaJornada = dayCutoffHour
    ? `la jornada de hoy (desde las ${horaDeCorte(dayCutoffHour)})`
    : "la jornada de hoy";
  const avisoPeriodo = (customFromDate || customToDate)
    ? "el rango elegido"
    : { jornada: etiquetaJornada, week: "la última semana", month: "el último mes" }[filterDate] || null;

  // Paginación
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const menuBar = {
    title: "Transacciones",
    buttons: [
      { label: "Exportar Excel", color: "green", onClick: exportFilteredTransactions },
      { label: "Recargar", color: "blue", onClick: fetchTransactions },
    ],
  };

  return (
    <div className="p-2 sm:p-4 space-y-3 sm:space-y-4">
      {/* Header */}
      {/* Desktop: barra normal */}
      <div className="hidden md:block">
        <ButtonBar
          title={menuBar.title}
          buttons={menuBar.buttons}
        />
      </div>
      {/* Móvil: stats (Total, Trans, Ventas, Créd, Reemb) como botones alargados */}
      <div className="block md:hidden bg-white p-4 rounded-lg shadow border border-gray-200 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-800">
            Transacciones
          </h2>
          <div className="flex items-center gap-2">
            {/* Mismo primary/outline que ButtonBar en tablet apaisada/escritorio (VARIANTES_BOTON). */}
            <button
              type="button"
              onClick={exportFilteredTransactions}
              className={`px-3 py-1.5 text-xs font-semibold rounded ${VARIANTES_BOTON.primary}`}
            >
              Excel
            </button>
            <button
              type="button"
              onClick={fetchTransactions}
              className={`px-3 py-1.5 text-xs font-semibold rounded ${VARIANTES_BOTON.outline}`}
            >
              Recargar
            </button>
          </div>
        </div>
        {loaded && filteredTransactions.length > 0 && (
          <div className="space-y-2">
            {/* Igual que en desktop: con "Todas" no hay total mezclado (ventas +
                aportaciones duplicaría el dinero del día); la fila "Trans" ya
                enseña el nº de movimientos. */}
            {filterType !== "all" && (
              <div className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-blue-50 border border-blue-200">
                <span className="text-xs text-blue-600 font-medium">Total</span>
                <span className="text-sm font-bold text-blue-900">€{stats.total}</span>
              </div>
            )}
            <div className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200">
              <span className="text-xs text-gray-600 font-medium">Trans</span>
              <span className="text-sm font-bold text-gray-900">{stats.count}</span>
            </div>
            <div className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-green-50 border border-green-200">
              <span className="text-xs text-green-600 font-medium">Ventas</span>
              <span className="text-sm font-bold text-green-900">{stats.sales}</span>
            </div>
            <div className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-purple-50 border border-purple-200">
              <span className="text-xs text-purple-600 font-medium">Créd</span>
              <span className="text-sm font-bold text-purple-900">{stats.credits}</span>
            </div>
            <div className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-red-50 border border-red-200">
              <span className="text-xs text-red-600 font-medium">Reemb</span>
              <span className="text-sm font-bold text-red-900">{stats.refunds}</span>
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {errorMessage && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{errorMessage}</span>
          <button
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setErrorMessage("")}
          >
            <span className="text-red-700">×</span>
          </button>
        </div>
      )}

      {/* Los chips y el Excel son los de lo CARGADO: con un periodo acotado no son el histórico, y
          quien venía de «todas las fechas» no tiene por qué saberlo (decisión 04-10-2026). */}
      {loaded && avisoPeriodo && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600" data-testid="aviso-periodo">
          <span>Mostrando solo {avisoPeriodo}: los totales y el Excel no son de todo el historial.</span>
          <button
            type="button"
            onClick={verTodasLasFechas}
            className="underline font-medium text-gray-700 hover:text-gray-900"
          >
            Ver todas las fechas
          </button>
        </div>
      )}

      {/* Estadísticas (solo desktop / tablet grande) */}
      {loaded && filteredTransactions.length > 0 && (
        <div className="hidden md:flex flex-wrap gap-2">
          {/* Con un solo tipo filtrado, sumar SÍ tiene sentido. Con "Todas", sumar
              ventas + aportaciones duplicaría el dinero del día: se enseña el
              desglose por tipo (importe · nº) en vez de un total mezclado. */}
          {filterType !== "all" ? (
            <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1 flex items-center gap-1.5">
              <span className="text-xs text-blue-600 font-medium whitespace-nowrap">Total:</span>
              <span className="text-sm font-bold text-blue-900 min-w-[4rem]">€{stats.total}</span>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1 flex items-center gap-1.5">
              <span className="text-xs text-blue-600 font-medium whitespace-nowrap" title="Ventas y aportaciones no se suman: el socio aporta y luego consume ese mismo dinero.">Movimientos:</span>
              <span className="text-sm font-bold text-blue-900 min-w-[3rem]">{stats.count}</span>
            </div>
          )}
          <div className="bg-green-50 border border-green-200 rounded px-2 py-1 flex items-center gap-1.5">
            <span className="text-xs text-green-600 font-medium whitespace-nowrap">Ventas:</span>
            <span className="text-sm font-bold text-green-900 min-w-[4rem]">€{stats.salesAmount}</span>
            <span className="text-xs text-green-700">({stats.sales})</span>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded px-2 py-1 flex items-center gap-1.5">
            <span className="text-xs text-purple-600 font-medium whitespace-nowrap">Aportaciones:</span>
            <span className="text-sm font-bold text-purple-900 min-w-[4rem]">€{stats.depositsAmount}</span>
            <span className="text-xs text-purple-700">({stats.credits})</span>
          </div>
          {stats.fees > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-center gap-1.5">
              <span className="text-xs text-amber-700 font-medium whitespace-nowrap" title="Deuda de inscripción cargada al saldo del socio: no es dinero cobrado todavía.">Cuotas:</span>
              <span className="text-sm font-bold text-amber-900 min-w-[4rem]">€{stats.feesAmount}</span>
              <span className="text-xs text-amber-700">({stats.fees})</span>
            </div>
          )}
          <div className="bg-red-50 border border-red-200 rounded px-2 py-1 flex items-center gap-1.5">
            <span className="text-xs text-red-600 font-medium whitespace-nowrap">Reemb:</span>
            <span className="text-sm font-bold text-red-900 min-w-[4rem]">€{stats.refundsAmount}</span>
            <span className="text-xs text-red-700">({stats.refunds})</span>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white p-3 md:p-4 rounded-lg shadow border border-gray-200 space-y-3">
        {/* Búsqueda */}
        <div>
          <input
            className="text-sm focus:outline-none active:outline-none border border-gray-300 w-full h-10 px-4 rounded"
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar por: ID, Nombre, Email, Tienda, Monto..."
            value={searchInput}
          />
        </div>

        {/* Filtros de tipo y fecha */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">Todas</option>
              <option value="venta">Ventas</option>
              <option value="credito">Aportaciones</option>
              <option value="reembolso">Reembolsos</option>
              <option value="cuota">Cuotas de inscripción</option>
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Pago</label>
            <select
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filterPayment}
              onChange={(e) => setFilterPayment(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="cash">Efectivo</option>
              <option value="card">Tarjeta</option>
              <option value="transfer">Transferencia</option>
              <option value="credit">Saldo del socio</option>
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Período</label>
            <select
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filterDate}
              onChange={(e) => {
                setFilterDate(e.target.value);
                setDraftFromDate("");
                setDraftToDate("");
                setCustomFromDate("");
                setCustomToDate("");
              }}
            >
              <option value="jornada">Jornada de hoy</option>
              <option value="week">Última semana</option>
              <option value="month">Último mes</option>
              <option value="all">Todas las fechas</option>
            </select>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-3">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => applyJornada(0)}
                  disabled={dayCutoffHour == null}
                  className="px-3 py-1.5 rounded-lg border border-lime-500 text-lime-700 text-xs font-medium hover:bg-lime-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="La jornada en curso según la hora de corte del club (Configuración)"
                >
                  Jornada de hoy
                </button>
                <button
                  type="button"
                  onClick={() => applyJornada(1)}
                  disabled={dayCutoffHour == null}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-xs font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="La jornada anterior completa, aunque cruce medianoche"
                >
                  Jornada de ayer
                </button>
                {dayCutoffHour != null && (
                  <span className="text-[11px] text-gray-400">corte {horaDeCorte(dayCutoffHour)}</span>
                )}
              </div>
              <label htmlFor="contabilidad-desde" className="block text-sm font-medium text-gray-700 mb-1">Desde (fecha y hora)</label>
              <input
                id="contabilidad-desde"
                type="datetime-local"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-500"
                value={draftFromDate}
                onChange={(e) => setDraftFromDate(e.target.value)}
              />
            </div>

            <div className="flex-1">
              <label htmlFor="contabilidad-hasta" className="block text-sm font-medium text-gray-700 mb-1">Hasta (fecha y hora)</label>
              <input
                id="contabilidad-hasta"
                type="datetime-local"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-500"
                value={draftToDate}
                onChange={(e) => setDraftToDate(e.target.value)}
              />
            </div>

            <div className="flex gap-2 lg:pb-[1px]">
              <button
                type="button"
                onClick={applyCustomDateRange}
                className="flex-1 lg:flex-none px-4 py-2 bg-lime-500 hover:bg-lime-600 text-white text-sm font-semibold rounded transition-colors"
              >
                Filtrar
              </button>
              <button
                type="button"
                onClick={clearCustomDateRange}
                className="flex-1 lg:flex-none px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded transition-colors"
              >
                Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading */}
      {!loaded && (
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Cargando transacciones...</p>
          </div>
        </div>
      )}

      {/* Transactions list */}
      {loaded && (
        <>
          <TransactionsBox transactions={paginatedTransactions} onReverse={setPorAnular} />
          
          {/* Paginación */}
          {totalPages > 1 && (
            <div className="bg-white p-3 md:p-4 rounded-lg shadow border border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                  Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredTransactions.length)} de {filteredTransactions.length}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-600"
                  >
                    Anterior
                  </button>
                  <span className="px-2 py-1.5 text-xs sm:text-sm text-gray-700 whitespace-nowrap">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-600"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* El nativo `window.confirm` se traga en las tablets del club, y este botón
          mueve DINERO: allí no hacía nada y sin decirlo. Mismo diálogo que el resto
          de la app (anular cuota, borrar producto…). */}
      <ConfirmDialog
        active={porAnular != null}
        title={porAnular ? planDeAnulacion(porAnular).titulo : ""}
        description={porAnular ? planDeAnulacion(porAnular).descripcion : null}
        note={porAnular ? planDeAnulacion(porAnular).nota || null : null}
        confirmLabel={porAnular ? planDeAnulacion(porAnular).boton : "Aceptar"}
        tone="danger"
        busy={anulando != null}
        onConfirm={() => porAnular && handleReverse(porAnular)}
        onCancel={() => setPorAnular(null)}
      />
    </div>
  );
}
